/**
 * 67.75: early designs for the ships in orbit (the owner, Oct 19, by voice: *"Maybe early designs for ships in orbit? … ships in
 * the Alien universe are pretty cool. They're pretty, like, seemingly, like, practical … in the Expanse, having, like, boarding
 * ships is cool … the kind of, like, space hacky bullshit that kind of works, just well enough … some of the ships from Cowboy
 * Bebop some of the larger spaceships were kind of cool … I'm kind of thinking like No Man's Sky scale a little bit for like
 * the size of the really large things. Because like there's got to be this plausible resupply or like supply and like orbital
 * transit, logistical transit believability"*). The setting (DECISIONS 210, 215): a backwater military corporation cum
 * extraction company, 200 years on; expeditionary ships that mostly carry the means to set up a colony or a mine, fight other
 * ships when they must, and drop pods and dropships to the ground.
 *
 * Step 1b, after the owner's look at step 1 (*"Ok I like the spaceships, don't love the boarding craft.  Carriers are probably
 * the best of them, but all close but probably not good enough."*) and his two references (a long container freighter: a drive
 * and reactor at one end with its coils bare, a thin spine, identical numbered containers hung along it with the company's
 * name; a chunky corporate hull: big armour slabs in the company's colour over brown working machinery, pale stripes, slit
 * lights, a logo). Targets, nothing traced. The language now, for every ship:
 *   - **the company's livery**: the team's colour the main block, a pale stripe of it, the company's name painted on the hull
 *     (a block-letter stencil, `hullName`) and its mark on a cream panel (`Livery`);
 *   - **armour over working machinery**: slabs in the livery with gaps and open undersides where the brown frames, pipes and
 *     boxes show; glowing slit lights; the drive's coils glowing where they are bare;
 *   - **modular cargo as identical containers**, numbered.
 * The looks:
 *   - **the expeditionary carrier**, a kilometre and more, in three variants: `freighter` (A: the drive and reactor block with
 *     its coils aft, a thin spine, eight identical containers hung under it, numbered, the foremost a hangar for the
 *     dropships), `carrier` (B: a chunky armoured hull, a carapace of three slabs and flank skirts over a machinery core, a
 *     hangar block and two engine pods under it, a sensor ring at the bow) and `trawler` (C: a foredeck the dropships land on,
 *     the bridge tower amidships, a spin ring aft — re-dressed in the livery);
 *   - **the escort**, 185 m (step 1c, toward the Expanse's Martian ships, less aggressive): a tall, narrow hull with its decks
 *     stacked along the thrust, the drive at the stern; vertical launch cells (no barrels) and ten point-defence mounts (small,
 *     fast-tracking six-barrel rotaries with sensor balls) spread round its corners, spine and keel; its hull number;
 *   - **the tender**, 38 m (the boarding craft's replacement: a utility craft that can also dock where it is not asked to): an
 *     armoured cab over a machinery belly, two manipulator arms, a docking collar, four thruster pods;
 *   - **the lighters**, the haulers that carry the vehicle frames (rendering/vehicleFrame.ts). Step 1c offered three; the
 *     owner (1d): *"I think I like lighter B the most, but a would be much more plausibly capable of landing."* So two jobs:
 *     `truss` (B: an open cage clamping six frames, engine pods at its corners) in orbit and ship to ship, and `lifter` (A: a
 *     squat lifting body, four frames inside, a rear ramp, lift fans in the belly) the one that lands. The tug (C) is dropped:
 *     the truss does the ship-to-ship run.
 * Each is one spec of primitives drawn in two tiers, both merged into one geometry (one draw): **full** (every part, the
 * lettering) and **far** (the masses only, coarse round parts: a few hundred triangles). Running lights (red to port, green to
 * starboard, white strobes, amber beacons, the team's ident light, bay floods) are listed per ship for the sky's one point
 * draw (rendering/orbitSky.ts).
 *
 * Metres (a person is 1.8, as the dropship's person units), +z the bow, +y dorsal (away from the planet in orbit), +x port.
 * Presentation only: no ship in orbit is a body yet. The sheet is design_sheets/orbit_ships.html.
 */
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { slab, top, front, type Pts } from './vehicleLooks';
import type { UnitPalette } from './unitPalette';

export type ShipKind = 'freighter' | 'carrier' | 'trawler' | 'escort' | 'tender' | 'lifter' | 'truss';
export const SHIP_KIND_LIST: readonly ShipKind[] = ['freighter', 'carrier', 'trawler', 'escort', 'tender', 'lifter', 'truss'];
export type ShipTier = 'full' | 'far';

/** The team-neutral paints: the cream of the panels, the machinery's browns, the glows; the team's three are its palette's. */
export const PAINT = {
  hazard: '#b5482c', amber: '#d08a2a', caution: '#d6b43a', rubber: '#2e2a26',
  cream: '#e4dac0', mach: '#6e4c34', machDk: '#3b2a1f', machLt: '#9a7352', metal: '#46484c', nozzle: '#2a2b2e',
  rad: '#34363a', window: '#ffd890', slit: '#e6f27a', coil: '#ff8a2a', bay: '#15171a', olive: '#5f6444', tan: '#8c7a58', slate: '#55606a',
} as const;
export type Paint = keyof typeof PAINT | 'team' | 'teamDk' | 'teamLt';

/** A company's livery on its ships: the name painted on the hulls, its mark (one of `MARKS`). */
export interface Livery { name: string; mark: number }
/** The name a company paints: its name in capitals, the legal suffix dropped, its first word when it is long. */
export function hullName(company: string): string {
  let n = company.toUpperCase().replace(/[^A-Z0-9& .-]/g, '').replace(/\s+(INC\.?|LLC|CO\.?|LTD\.?|GROUP)$/, '').trim();
  if (n.length > 14) n = n.split(' ')[0];
  return n;
}

export type LampKind = 'red' | 'green' | 'strobe' | 'beacon' | 'ident' | 'flood';
/** Each lamp's look: colour (ident: the team's light), size in pixels, blink (per second, share lit; 0: steady). */
export const LAMPS: Record<LampKind, { color: string; px: number; rate: number; duty: number }> = {
  red: { color: '#ff3a2a', px: 2.6, rate: 0, duty: 1 },
  green: { color: '#3aff6a', px: 2.6, rate: 0, duty: 1 },
  strobe: { color: '#ffffff', px: 3.6, rate: 0.9, duty: 0.07 },
  beacon: { color: '#ffb030', px: 2.4, rate: 0.5, duty: 0.35 },
  ident: { color: '#ffffff', px: 3.0, rate: 0, duty: 1 },
  flood: { color: '#fff1d0', px: 2.2, rate: 0, duty: 1 },
};
export interface Lamp { x: number; y: number; z: number; kind: LampKind; phase: number }

// ---------------------------------------------------------------------------
// The stencil: 5 × 7 block letters, and three company marks
// ---------------------------------------------------------------------------

const GLYPHS: Record<string, string> = {
  A: '01110 10001 10001 11111 10001 10001 10001', B: '11110 10001 10001 11110 10001 10001 11110', C: '01110 10001 10000 10000 10000 10001 01110',
  D: '11110 10001 10001 10001 10001 10001 11110', E: '11111 10000 10000 11110 10000 10000 11111', F: '11111 10000 10000 11110 10000 10000 10000',
  G: '01110 10001 10000 10111 10001 10001 01111', H: '10001 10001 10001 11111 10001 10001 10001', I: '01110 00100 00100 00100 00100 00100 01110',
  J: '00111 00010 00010 00010 00010 10010 01100', K: '10001 10010 10100 11000 10100 10010 10001', L: '10000 10000 10000 10000 10000 10000 11111',
  M: '10001 11011 10101 10101 10001 10001 10001', N: '10001 10001 11001 10101 10011 10001 10001', O: '01110 10001 10001 10001 10001 10001 01110',
  P: '11110 10001 10001 11110 10000 10000 10000', Q: '01110 10001 10001 10001 10101 10010 01101', R: '11110 10001 10001 11110 10100 10010 10001',
  S: '01111 10000 10000 01110 00001 00001 11110', T: '11111 00100 00100 00100 00100 00100 00100', U: '10001 10001 10001 10001 10001 10001 01110',
  V: '10001 10001 10001 10001 10001 01010 00100', W: '10001 10001 10001 10101 10101 10101 01010', X: '10001 10001 01010 00100 01010 10001 10001',
  Y: '10001 10001 01010 00100 00100 00100 00100', Z: '11111 00001 00010 00100 01000 10000 11111',
  0: '01110 10001 10011 10101 11001 10001 01110', 1: '00100 01100 00100 00100 00100 00100 01110', 2: '01110 10001 00001 00010 00100 01000 11111',
  3: '11111 00010 00100 00010 00001 10001 01110', 4: '00010 00110 01010 10010 11111 00010 00010', 5: '11111 10000 11110 00001 00001 10001 01110',
  6: '00110 01000 10000 11110 10001 10001 01110', 7: '11111 00001 00010 00100 01000 01000 01000', 8: '01110 10001 10001 01110 10001 10001 01110',
  9: '01110 10001 10001 01111 00001 00010 01100', '&': '01100 10010 10100 01000 10101 10010 01101', '.': '00000 00000 00000 00000 00000 01100 01100',
  '-': '00000 00000 00000 11111 00000 00000 00000', ' ': '00000 00000 00000 00000 00000 00000 00000',
};
/** The companies' marks, 7 × 7: a cut gem, a double chevron, a rising wing. */
export const MARKS = [
  '0001000 0011100 0111110 1111111 0111110 0011100 0001000',
  '1000001 1100011 0110110 0011100 1000001 1100011 0110110',
  '0000011 0000111 0001110 0011110 0111100 1111000 1100000',
];
/** The bitmap's lit runs per row (top row first): [row, first column, past the last]. */
function runs(rows: string[]): [number, number, number][] {
  const out: [number, number, number][] = [];
  rows.forEach((r, y) => { for (let x = 0; x < r.length;) { if (r[x] !== '1') { x++; continue; } const a = x; while (r[x] === '1') x++; out.push([y, a, x]); } });
  return out;
}
/** The stencil's width for `n` characters `h` tall (a character is 5 of 7 rows wide, one column between). */
export const stencilWidth = (n: number, h: number) => ((6 * n - 1) * h) / 7;

// ---------------------------------------------------------------------------
// The spec
// ---------------------------------------------------------------------------

type Tier = 'both' | ShipTier;
interface Part { make: (far: boolean) => THREE.BufferGeometry; c: Paint; glow: number; tier: Tier }
interface Opt { tier?: Tier; glow?: number; rx?: number; ry?: number; rz?: number }

const tmpE = new THREE.Euler(), tmpQ = new THREE.Quaternion(), tmpM = new THREE.Matrix4(), ONE = new THREE.Vector3(1, 1, 1);
function placed(g: THREE.BufferGeometry, x: number, y: number, z: number, o: Opt = {}): THREE.BufferGeometry {
  tmpQ.setFromEuler(tmpE.set(o.rx ?? 0, o.ry ?? 0, o.rz ?? 0));
  return g.applyMatrix4(tmpM.compose(new THREE.Vector3(x, y, z), tmpQ, ONE));
}
/** Turn a geometry built along y to lie along `axis` (+y → +axis). */
function along(g: THREE.BufferGeometry, axis: 'x' | 'y' | 'z'): THREE.BufferGeometry {
  if (axis === 'z') g.rotateX(Math.PI / 2); else if (axis === 'x') g.rotateZ(-Math.PI / 2);
  return g;
}
/** A mesh's geometry with its own placement folded in (the vehicle kit's extrusions add a mesh to a parent). */
function taken(add: (parent: THREE.Object3D) => THREE.Mesh): THREE.BufferGeometry {
  const holder = new THREE.Group(), m = add(holder);
  m.updateMatrix();
  const g = m.geometry.applyMatrix4(m.matrix);
  (m.material as THREE.Material).dispose();
  return g;
}

/** What a ship is built carrying: `cargo` false leaves a lighter's slots empty (the frames sheet hangs the real frames there). */
export interface SpecOptions {
  cargo: boolean;
  /** 69.10: false leaves the lighter's ramp (and its shut door) out, for play to hinge its own; absent or true, drawn as before */
  ramp?: boolean;
  /** 69.10: false leaves lighter A's four swivelling nacelles out (their stubs stay), for play to turn its own; absent, drawn aft */
  nacelles?: boolean;
}
/** One ship's (or prop's) parts and lamps, as its builder lays them down. */
export class Spec {
  parts: Part[] = [];
  lamps: Lamp[] = [];
  private xf: THREE.Matrix4 | null = null;
  constructor(readonly livery: Livery, readonly opts: SpecOptions = { cargo: true }) {}
  private part(make: Part['make'], c: Paint, o: Opt): void {
    const xf = this.xf;
    this.parts.push({ make: xf ? far => make(far).applyMatrix4(xf) : make, c, glow: o.glow ?? 0, tier: o.tier ?? 'both' });
  }
  /** Lay down `fn`'s parts through the transform `m` (a mount on a sloped face, say). */
  within(m: THREE.Matrix4, fn: () => void): void {
    const prev = this.xf;
    this.xf = prev ? prev.clone().multiply(m) : m.clone();
    fn();
    this.xf = prev;
  }
  /** A cross-section (x, y pairs) extruded `d` along the ship, centred at `z`; `chamfer` bevels its edges. */
  section(pts: Pts, d: number, z: number, c: Paint, o: Opt & { chamfer?: number } = {}): void {
    this.part(() => taken(p => front(p, pts, d, '#ffffff', z, o.chamfer ?? 0)), c, o);
  }
  box(w: number, h: number, d: number, x: number, y: number, z: number, c: Paint, o: Opt = {}): void {
    this.part(() => placed(new THREE.BoxGeometry(w, h, d), x, y, z, o), c, o);
  }
  /** A cylinder or cone along `axis`: radius `r` at its +axis end, `r2` (default `r`) at the other. */
  cyl(r: number, len: number, axis: 'x' | 'y' | 'z', x: number, y: number, z: number, c: Paint, o: Opt & { r2?: number; seg?: number; farSeg?: number } = {}): void {
    this.part(far => placed(along(new THREE.CylinderGeometry(r, o.r2 ?? r, len, far ? (o.farSeg ?? 5) : (o.seg ?? 12)), axis), x, y, z, o), c, o);
  }
  /** A rod of radius `r` from `a` to `b` (any direction). */
  rod(a: THREE.Vector3, b: THREE.Vector3, r: number, c: Paint, o: Opt & { seg?: number } = {}): void {
    const d = b.clone().sub(a), len = d.length(), q = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), d.normalize());
    const mid = a.clone().add(b).multiplyScalar(0.5);
    this.part(far => new THREE.CylinderGeometry(r, r, len, far ? 4 : (o.seg ?? 6)).applyQuaternion(q).translate(mid.x, mid.y, mid.z), c, o);
  }
  sphere(r: number, x: number, y: number, z: number, c: Paint, o: Opt = {}): void {
    this.part(far => placed(new THREE.SphereGeometry(r, far ? 5 : 12, far ? 3 : 8), x, y, z, o), c, o);
  }
  torus(r: number, t: number, axis: 'x' | 'y' | 'z', x: number, y: number, z: number, c: Paint, o: Opt = {}): void {
    // (a torus lies in xy round z: turned so it rings `axis`)
    this.part(far => {
      const g = new THREE.TorusGeometry(r, t, far ? 3 : 6, far ? 10 : 28);
      if (axis === 'y') g.rotateX(Math.PI / 2); else if (axis === 'x') g.rotateY(Math.PI / 2);
      return placed(g, x, y, z, o);
    }, c, o);
  }
  /** A side profile (z, y pairs) extruded `w` across, centred on `x`; `chamfer` bevels its edges. */
  side(pts: Pts, w: number, x: number, c: Paint, o: Opt & { chamfer?: number } = {}): void {
    this.part(() => taken(p => slab(p, pts, w, '#ffffff', x, o.chamfer ?? 0)), c, o);
  }
  /** A plan profile (x, z pairs) extruded `h` up from `y`. */
  plan(pts: Pts, h: number, y: number, c: Paint, o: Opt & { chamfer?: number } = {}): void {
    this.part(() => taken(p => top(p, pts, h, '#ffffff', y, o.chamfer ?? 0)), c, o);
  }
  /**
   * Paint a bitmap (rows of '0'/'1', top first) on both flanks: on the face at ±`x`, centred at (`y`, `z`), `h` tall, reading
   * bow to stern on the starboard face and stern to bow on the port one (so it reads from either side). The full tier only.
   */
  private bitmap(rows: string[][], x: number, y: number, z: number, h: number, c: Paint, glow: number, only?: 1 | -1): void {
    const px = h / 7, cols = rows.length * 6 - 1, w = cols * px;
    const quads: [number, number, number, number][] = [];   // (u0, u1, v0, v1) on the face, u along the reading
    rows.forEach((g, k) => { for (const [row, a, b] of runs(g)) quads.push([k * 6 * px + a * px - w / 2, k * 6 * px + b * px - w / 2, h / 2 - (row + 1) * px, h / 2 - row * px]); });
    this.part(() => {
      const pos: number[] = [], nor: number[] = [], lift = Math.max(0.02, h * 0.012);
      for (const side of only ? [only] : [1, -1]) {
        // starboard (−x): reading +z; port (+x): reading −z — the face's normal is ±x either way
        const fx = side * (x + lift), dz = side > 0 ? -1 : 1;
        for (const [u0, u1, v0, v1] of quads) {
          const za = z + u0 * dz, zb = z + u1 * dz, ya = y + v0, yb = y + v1;
          const quad = [[fx, ya, za], [fx, ya, zb], [fx, yb, zb], [fx, ya, za], [fx, yb, zb], [fx, yb, za]];
          // (counter-clockwise seen from outside on either face: the reading direction flips with the side)
          for (const v of quad) { pos.push(...v); nor.push(side, 0, 0); }
        }
      }
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
      g.setAttribute('normal', new THREE.Float32BufferAttribute(nor, 3));
      return g;
    }, c, { tier: 'full', glow });
  }
  /** Paint `text` in the stencil on both flanks at ±`x` (or only the one `only` names), centred at (`y`, `z`), at most `h` tall and `maxW` long. */
  text(text: string, x: number, y: number, z: number, h: number, maxW: number, c: Paint, glow = 0, only?: 1 | -1): void {
    const chars = [...text].filter(ch => GLYPHS[ch]);
    const fit = Math.min(h, (maxW * 7) / (6 * chars.length - 1));
    this.bitmap(chars.map(ch => GLYPHS[ch].split(' ')), x, y, z, fit, c, glow, only);
  }
  /** The company's name, painted. */
  name(x: number, y: number, z: number, h: number, maxW: number, c: Paint = 'cream', only?: 1 | -1): void { this.text(this.livery.name, x, y, z, h, maxW, c, 0, only); }
  /** The company's mark on a cream panel, on both flanks. */
  mark(x: number, y: number, z: number, h: number, panel = true): void {
    if (panel) this.box(2 * x + 0.04 * h, h * 1.3, h * 1.3, 0, y, z, 'cream');
    const g = MARKS[this.livery.mark % MARKS.length].split(' ');
    // (a 7-wide mark: drawn as one 7-column glyph, 7 rows tall)
    const px = h / 7;
    const quads = runs(g).map(([row, a, b]) => [a * px - h / 2, b * px - h / 2, h / 2 - (row + 1) * px, h / 2 - row * px]);
    this.part(() => {
      const pos: number[] = [], nor: number[] = [], lift = Math.max(0.03, h * 0.04);
      for (const side of [1, -1]) {
        const fx = side * (x + 0.02 * h + lift), dz = side > 0 ? -1 : 1;
        for (const [u0, u1, v0, v1] of quads) {
          const za = z + u0 * dz, zb = z + u1 * dz, ya = y + v0, yb = y + v1;
          const quad = [[fx, ya, za], [fx, ya, zb], [fx, yb, zb], [fx, ya, za], [fx, yb, zb], [fx, yb, za]];
          for (const v of quad) { pos.push(...v); nor.push(side, 0, 0); }
        }
      }
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
      geo.setAttribute('normal', new THREE.Float32BufferAttribute(nor, 3));
      return geo;
    }, 'team', { tier: 'full' });
  }
  lamp(x: number, y: number, z: number, kind: LampKind): void { this.lamps.push({ x, y, z, kind, phase: (this.lamps.length * 0.618) % 1 }); }
  /** A lamp pair at ±x: red to port (+x), green to starboard. */
  sides(x: number, y: number, z: number): void { this.lamp(x, y, z, 'red'); this.lamp(-x, y, z, 'green'); }
  /** Slit lights: a glowing strip on both flanks at ±x. */
  slit(x: number, y: number, z: number, len: number, h = Math.max(0.4, len * 0.04)): void { for (const sx of [-1, 1]) this.box(0.6, h, len, sx * x, y, z, 'slit', { tier: 'full', glow: 1 }); }
}

// a deterministic scatter of machinery boxes (the gear the armour doesn't cover)
function gear(s: Spec, n: number, seed: number, at: (k: number, r: () => number) => [number, number, number, number, number, number]): void {
  let q = seed;
  const r = () => { q = (q * 16807) % 2147483647; return q / 2147483647; };
  for (let k = 0; k < n; k++) { const [w, h, d, x, y, z] = at(k, r); s.box(w, h, d, x, y, z, k % 3 === 0 ? 'machLt' : k % 3 === 1 ? 'machDk' : 'mach', { tier: 'full' }); }
}

// ---------------------------------------------------------------------------
// The carriers
// ---------------------------------------------------------------------------

/** The container: the freighter hangs eight, the lighter carries two of a smaller size. */
export const CONTAINER = { length: 120, width: 56, height: 78, gap: 14 };
/** One container hung under a spine at `z`: ribbed, framed, numbered on both flanks; `name` paints the company's name on it. */
function container(s: Spec, z: number, n: number, k: { w: number; h: number; l: number; y: number }, name: boolean): void {
  s.box(k.w, k.h, k.l, 0, k.y, z, 'team');
  for (const r of [-0.375, -0.125, 0.125, 0.375]) s.box(k.w + k.w * 0.03, k.h * 0.94, k.l * 0.03, 0, k.y, z + r * k.l, 'teamDk', { tier: 'full' });
  s.box(k.w * 1.04, k.h * 0.08, k.l * 1.02, 0, k.y - k.h * 0.5, z, 'machDk', { tier: 'full' });
  s.box(k.w * 0.36, k.h * 0.1, k.l * 0.84, 0, k.y + k.h * 0.53, z, 'mach', { tier: 'full' });
  s.box(k.w * 1.02, k.h * 0.06, k.l * 0.12, 0, k.y + k.h * 0.36, z - k.l * 0.42, 'teamLt', { tier: 'full' });
  s.text(String(n).padStart(2, '0'), k.w * 0.5 + k.w * 0.016, k.y + k.h * 0.22, z - k.l * 0.25, k.h * 0.18, k.l * 0.2, 'cream');
  if (name) s.name(k.w * 0.5 + k.w * 0.016, k.y - k.h * 0.12, z + k.l * 0.08, k.h * 0.17, k.l * 0.7);
}

/** A: the container freighter, ~1,430 m: a drive and reactor with its coils bare, a thin spine, eight containers under it. */
function freighter(s: Spec): void {
  // the drive: two bells, the thrust plate, the coil stack (bare and glowing), its cage, the shroud over its back half
  for (const sx of [-1, 1]) { s.cyl(28, 70, 'z', sx * 40, 0, -705, 'nozzle', { r2: 44 }); s.cyl(38, 1, 'z', sx * 40, 0, -740.5, 'bay', { tier: 'full' }); }
  s.box(170, 120, 14, 0, 0, -663, 'machDk');
  s.cyl(30, 150, 'z', 0, 0, -580, 'machDk');
  for (let k = 0; k < 14; k++) s.torus(38, 3.4, 'z', 0, 0, -645 + k * 9.6, 'coil', { tier: 'full', glow: 1 });
  s.cyl(41, 132, 'z', 0, 0, -580, 'coil', { tier: 'far', glow: 0.8, farSeg: 6 });
  for (const sx of [-1, 1]) for (const sy of [-1, 1]) s.box(7, 7, 170, sx * 52, sy * 48, -580, 'mach', { tier: 'full' });
  for (const z of [-650, -580, -510]) s.box(116, 8, 8, 0, 48, z, 'mach', { tier: 'full' }), s.box(116, 8, 8, 0, -48, z, 'mach', { tier: 'full' });
  s.box(126, 16, 80, 0, 60, -610, 'team');
  s.box(128, 4, 82, 0, 60, -610, 'teamLt', { tier: 'full' });
  for (const sx of [-1, 1]) { s.cyl(16, 90, 'z', sx * 70, 34, -545, 'cream'); s.box(30, 40, 54, sx * 74, -24, -540, 'mach'); }
  gear(s, 24, 41, (_k, r) => [6 + r() * 14, 6 + r() * 16, 8 + r() * 20, (r() < 0.5 ? -1 : 1) * (60 + r() * 22), -40 + r() * 70, -640 + r() * 130]);
  // the reactor block: the livery, a stripe, the name, slits
  s.box(110, 100, 64, 0, 0, -476, 'team');
  s.box(112, 14, 66, 0, 32, -476, 'teamLt', { tier: 'full' });
  s.name(55, -12, -476, 22, 56);
  s.slit(55.5, 14, -476, 50, 2.2);
  // the spine, its rail and conduit, the junction under it, a tank
  s.box(16, 16, 1170, 0, 0, 141, 'machDk');
  s.box(24, 5, 1170, 0, 10.5, 141, 'teamDk');
  s.cyl(3.5, 1170, 'z', 6, 15, 141, 'mach', { tier: 'full', seg: 6 });
  s.box(60, 50, 60, 0, -14, -410, 'mach');
  s.sphere(18, 0, -46, -410, 'cream');
  gear(s, 10, 7, (_k, r) => [5 + r() * 10, 5 + r() * 10, 5 + r() * 12, -32 + r() * 64, -40 + r() * 52, -442 + r() * 64]);
  // the containers: eight, identical, numbered from the stern; the name on the 2nd and 6th; the foremost a hangar
  const k = { w: CONTAINER.width, h: CONTAINER.height, l: CONTAINER.length, y: -8 - CONTAINER.height / 2 };
  for (let i = 0; i < 8; i++) {
    const z = -300 + i * (CONTAINER.length + CONTAINER.gap);
    container(s, z, i + 1, k, i === 1 || i === 5);
    s.box(10, 8, 10, 0, -6, z - 40, 'mach', { tier: 'full' }); s.box(10, 8, 10, 0, -6, z + 40, 'mach', { tier: 'full' });
  }
  const zh = -300 + 7 * (CONTAINER.length + CONTAINER.gap);
  s.box(44, 60, 1, 0, k.y - 4, zh + 60.6, 'bay');
  s.lamp(22, k.y - 34, zh + 61, 'flood'); s.lamp(-22, k.y - 34, zh + 61, 'flood');
  // the bow cap
  s.box(30, 24, 30, 0, 4, 728, 'team');
  s.box(31, 3, 31, 0, 10, 728, 'teamLt', { tier: 'full' });
  s.slit(15.5, 0, 730, 20, 1.4);
  s.cyl(0.8, 40, 'y', 8, 36, 724, 'metal', { tier: 'full', seg: 4 });
  s.sides(64, 0, -476);
  s.lamp(0, 18, 744, 'strobe'); s.lamp(0, 70, -610, 'strobe'); s.lamp(0, -88, -300, 'strobe');
  for (let z = -300; z <= 500; z += 200) s.lamp(0, 14, z, 'beacon');
  s.lamp(0, 17, 728, 'ident');
}

/** B: the armoured carrier, ~1,190 m: a carapace and skirts in the livery over a machinery core, a hangar and engine pods under it. */
function carrier(s: Spec): void {
  // the machinery core, its frames, pipes and gear
  s.box(150, 120, 1000, 0, 0, -20, 'mach');
  for (let z = -500; z <= 460; z += 48) s.box(156, 124, 5, 0, -2, z, 'machDk', { tier: 'full' });
  for (const sx of [-1, 1]) for (const y of [-34, -12]) s.cyl(4, 920, 'z', sx * 78, y, -20, 'machLt', { tier: 'full', seg: 6 });
  gear(s, 48, 3, (_k, r) => [6 + r() * 18, 8 + r() * 22, 10 + r() * 40, (r() < 0.5 ? -1 : 1) * (76 + r() * 6), -55 + r() * 40, -480 + r() * 940]);
  // the carapace: three slabs with gaps between, their ends chamfered; the bow's sloped
  const sect = (z0: number, z1: number, t: number): Pts => [z0, 52, z1, 52, z1, t - 14, z1 - 18, t, z0 + 12, t, z0, t - 10];
  s.side(sect(-525, -242, 112), 196, 0, 'team', { chamfer: 6, tier: 'full' });
  s.side(sect(-226, 160, 124), 200, 0, 'team', { chamfer: 6, tier: 'full' });
  s.side([176, 52, 520, 52, 560, 18, 560, 40, 524, 96, 176, 106], 192, 0, 'team', { chamfer: 6, tier: 'full' });
  s.box(196, 58, 283, 0, 82, -383, 'team', { tier: 'far' }); s.box(200, 70, 386, 0, 88, -33, 'team', { tier: 'far' });
  s.side([176, 52, 520, 52, 560, 18, 560, 40, 524, 96, 176, 106], 192, 0, 'team', { tier: 'far' });
  // pale stripes across the middle slab and along the skirts
  for (const z of [-40, 10]) s.box(204, 3, 30, 0, 123.5, z, 'teamLt');
  s.box(206, 40, 30, 0, 100, -40, 'teamLt', { tier: 'full' }); s.box(206, 40, 30, 0, 100, 10, 'teamLt', { tier: 'full' });
  // the skirts over the flanks: three a side, the brown showing between and below
  for (const sx of [-1, 1]) {
    s.box(10, 84, 300, sx * 101, 14, 340, 'team');
    s.box(10, 64, 250, sx * 101, 24, -30, 'team');
    s.box(10, 78, 240, sx * 101, 16, -390, 'team');
    s.box(11, 8, 300, sx * 101, 46, 340, 'teamLt', { tier: 'full' });
    s.box(11.5, 16, 80, sx * 101.3, 16, -50, 'slit', { tier: 'full', glow: 1 });
  }
  for (const z of [240, 300, 420]) s.slit(106.4, -20, z, 30, 2.4);
  s.name(106.4, 14, 340, 26, 230);
  s.mark(106.4, 12, -400, 44);
  s.text('B-07', 106.4, 50, -330, 14, 60, 'cream');
  // the bridge on the carapace, the sensor ring at the bow
  s.box(50, 24, 40, 0, 136, 120, 'team');
  s.box(51, 4, 41, 0, 140, 120, 'slit', { tier: 'full', glow: 1 });
  s.cyl(0.8, 40, 'y', -14, 166, 112, 'metal', { tier: 'full', seg: 4 });
  s.torus(44, 8, 'z', 0, 0, 482, 'machLt');
  s.cyl(40, 4, 'z', 0, 0, 480, 'machDk');
  // underneath: the hangar block and its bays, a belly plate, two engine pods on pylons
  s.box(120, 50, 300, 0, -85, 120, 'machDk');
  for (const bx of [-30, 30]) for (const bz of [60, 170]) { s.box(32, 1, 44, bx, -110.6, bz, 'bay'); s.lamp(bx - 16, -112, bz - 22, 'flood'); }
  s.box(128, 12, 110, 0, -114, 255, 'team');
  s.box(130, 3, 112, 0, -108, 255, 'teamLt', { tier: 'full' });
  for (const sx of [-1, 1]) {
    s.box(14, 44, 120, sx * 64, -72, -330, 'mach', { tier: 'full' });
    s.cyl(28, 260, 'z', sx * 72, -100, -330, 'team', { seg: 10 });
    s.cyl(22, 1, 'z', sx * 72, -100, -199.4, 'machDk', { tier: 'full', seg: 10 });
    s.torus(25, 3, 'z', sx * 72, -100, -462, 'coil', { tier: 'full', glow: 1 });
    s.box(4, 4, 200, sx * 100.5, -100, -330, 'teamLt', { tier: 'full' });
  }
  // the drive aft
  s.box(170, 140, 60, 0, 10, -555, 'machDk');
  for (const [x, y] of [[-50, 30], [50, 30], [0, -30]]) { s.cyl(24, 50, 'z', x, y, -605, 'nozzle', { r2: 36 }); s.torus(36, 3, 'z', x, y, -630, 'coil', { tier: 'full', glow: 1 }); }
  s.sides(108, 40, 400);
  s.lamp(0, 150, 120, 'ident'); s.lamp(0, 0, 532, 'strobe'); s.lamp(0, 90, -585, 'strobe'); s.lamp(0, -130, -330, 'strobe');
  for (const z of [-380, -40, 330]) s.lamp(0, 126, z, 'beacon');
}

/** C: the trawler, ~1,170 m, in the livery: a foredeck the dropships land on, the bridge tower amidships, a spin ring aft. */
function trawler(s: Spec): void {
  s.plan([-90, 50, 90, 50, 90, 420, 50, 550, -50, 550, -90, 420], 14, 30, 'cream', { chamfer: 2 });
  s.side([50, -40, 450, -40, 550, 0, 550, 30, 50, 30], 120, 0, 'team', { chamfer: 4 });
  s.box(124, 8, 380, 0, 4, 250, 'teamLt');
  for (const [x, z] of [[-45, 150], [45, 150], [-45, 280], [45, 280]]) {
    s.box(34, 0.6, 34, x, 44.3, z, 'team', { tier: 'full' });
    s.box(26, 0.7, 26, x, 44.4, z, 'cream', { tier: 'full' });
    s.lamp(x - 17, 45, z - 17, 'flood'); s.lamp(x + 17, 45, z + 17, 'flood');
  }
  // the under-deck machinery, open between the hull and the deck's overhang
  gear(s, 22, 19, (_k, r) => [6 + r() * 12, 6 + r() * 14, 10 + r() * 30, (r() < 0.5 ? -1 : 1) * (62 + r() * 24), 12 + r() * 16, 80 + r() * 380]);
  s.name(60, -12, 280, 20, 300);
  // the superstructure, the tower and its bridge
  s.box(130, 110, 200, 0, 30, -50, 'team');
  s.box(132, 12, 202, 0, 60, -50, 'teamLt');
  s.mark(65.5, 20, -90, 40);
  s.slit(65.6, 40, 10, 60, 2.4);
  s.box(70, 60, 60, 0, 115, -10, 'cream');
  s.box(71, 8, 61, 0, 128, -10, 'window', { tier: 'full', glow: 1 });
  s.box(90, 10, 40, 0, 150, 0, 'team');
  s.cyl(1, 40, 'y', -20, 175, -20, 'metal', { tier: 'full', seg: 4 });
  // the aft hull (machinery), the ring and its spokes, the tanks, the fins, the drive
  s.cyl(48, 400, 'z', 0, 0, -350, 'mach');
  for (let z = -530; z <= -170; z += 40) s.torus(49, 2.4, 'z', 0, 0, z, 'machDk', { tier: 'full' });
  s.torus(130, 16, 'z', 0, 0, -330, 'cream');
  s.torus(130, 16.6, 'z', 0, 0, -330, 'team', { tier: 'full', rx: 0 });
  s.box(10, 228, 12, 0, 0, -330, 'machLt'); s.box(228, 10, 12, 0, 0, -330, 'machLt');
  for (let k = 0; k < 12; k++) { const a = (k / 12) * Math.PI * 2; s.box(6, 4, 34, Math.cos(a) * 130, Math.sin(a) * 130, -330, 'window', { tier: 'full', glow: 0.8, rz: a }); }
  for (const [x, y] of [[62, 0], [-62, 0], [0, 62], [0, -62]]) s.cyl(26, 160, 'z', x, y, -470, 'cream');
  for (const sy of [-1, 1]) s.box(2, 200, 160, 0, sy * 150, -470, 'rad');
  for (const [x, y] of [[0, 30], [-28, -16], [28, -16]]) { s.cyl(18, 50, 'z', x, y, -575, 'nozzle', { r2: 28 }); s.torus(28, 2.4, 'z', x, y, -600, 'coil', { tier: 'full', glow: 1 }); }
  s.sides(92, 40, 420);
  s.lamp(0, 250, -470, 'strobe'); s.lamp(0, -250, -470, 'strobe'); s.lamp(0, 44, 550, 'strobe');
  for (let k = 0; k < 4; k++) { const a = (k / 4) * Math.PI * 2 + 0.4; s.lamp(Math.cos(a) * 146, Math.sin(a) * 146, -330, 'beacon'); }
  s.lamp(0, 156, 0, 'ident');
}

// ---------------------------------------------------------------------------
// The small craft
// ---------------------------------------------------------------------------

/** The escort's cross-section: a chamfered box taller than it is wide (24 × 28 m), scaled `k`. */
const ESCORT_SECTION = (k: number): Pts => [-12, -9, -7, -14, 7, -14, 12, -9, 12, 9, 7, 14, -7, 14, -12, 9].map(v => v * k);
/**
 * The escort, 185 m, after the owner: *"I think I want to lean a little more expanse / martian naval vessel for the escort, not
 * as aggressive but closer in that direction especially wrt layout and pdcs"* (and before it: *"do like the vls type thing"*).
 * The thrust-axis layout: a tall, narrow hull with its decks stacked along the thrust (deck bands every 8 m), the drive at the
 * stern, the nose stepping down; armour in the livery fore and aft, the machinery bare at the waist. No guns but the launch
 * cells (two dorsal banks, flank cells) and ten point-defence mounts spread round the hull — on its four chamfered corners fore
 * and aft, on the spine and the keel — for overlapping arcs. Flatter and plainer than a warship. Nothing traced.
 */
function escort(s: Spec): void {
  // the hull: aft armour, the bare waist, fore armour, the nose in two steps; the reactor and the drive
  s.section(ESCORT_SECTION(1), 60, -50, 'team', { chamfer: 1 });
  s.section(ESCORT_SECTION(0.78), 16, -12, 'mach');
  gear(s, 20, 31, (_k, r) => [1.5 + r() * 3, 1.5 + r() * 4, 2 + r() * 5, (r() < 0.5 ? -1 : 1) * (8.6 + r() * 1.4), -9 + r() * 18, -19 + r() * 14]);
  for (const z of [-17, -12, -7]) s.section(ESCORT_SECTION(0.86), 0.8, z, 'machDk', { tier: 'full' });
  s.section(ESCORT_SECTION(1), 56, 24, 'team', { chamfer: 1 });
  s.section(ESCORT_SECTION(0.76), 16, 60, 'team', { chamfer: 0.8 });
  s.section(ESCORT_SECTION(0.5), 8, 72, 'team', { chamfer: 0.6 });
  s.section(ESCORT_SECTION(0.88), 10, -85, 'machDk');
  s.cyl(8, 16, 'z', 0, 0, -98, 'nozzle', { r2: 13, seg: 16 });
  s.torus(12.6, 0.8, 'z', 0, 0, -105.5, 'coil', { tier: 'full', glow: 1 });
  // the decks along the thrust: a band every 8 m; the pale stripes; the name and number; slit windows at the nose
  for (let z = -76; z <= 48; z += 8) if (z < -21 || z > -3) s.section(ESCORT_SECTION(1.012), 0.5, z, 'teamDk', { tier: 'full' });
  for (const z of [-40, 36]) s.section(ESCORT_SECTION(1.02), 3, z, 'teamLt');
  s.name(12.06, 2, 14, 6, 34);
  s.text('E-14', 12.06, 2, -54, 6, 20, 'cream');
  s.mark(12.06, 1.5, -32, 5);
  s.slit(9.2, 4, 62, 10, 0.6);
  s.slit(6.2, 3, 73, 5, 0.5);
  // the launch cells: two dorsal banks of 4 × 8 hatches (every seventh open), two rows along each flank aft
  let n = 0;
  for (const z0 of [0, -72]) {
    s.box(15, 0.4, 27, 0, 14.1, z0 + 12, 'cream');
    for (let r = 0; r < 8; r++) for (let c = 0; c < 4; c++) {
      const x = -4.8 + c * 3.2, z = z0 + r * 3.3;
      if (++n % 7 === 0) { s.box(2.7, 0.2, 2.7, x, 14.35, z, 'bay', { tier: 'full' }); s.box(2.7, 0.3, 2.7, x, 15.6, z - 0.95, 'teamDk', { tier: 'full', rx: -1.15 }); }
      else s.box(2.7, 0.3, 2.7, x, 14.45, z, 'teamDk', { tier: 'full' });
    }
  }
  for (const sx of [-1, 1]) for (let k = 0; k < 10; k++) for (let r = 0; r < 2; r++) s.box(0.4, 2.4, 2.4, sx * 12.1, -5 + r * 3, -74 + k * 3.2, 'teamDk', { tier: 'full' });
  // point defence: the four corners fore and aft, the spine, the keel — each mount turned out of its face
  const c45 = Math.SQRT1_2;
  for (const sx of [-1, 1]) for (const sy of [-1, 1]) for (const [z, yaw] of [[42, 0.55], [-66, Math.PI - 0.55]] as const) pdcOn(s, new THREE.Vector3(sx * 9.6, sy * 11.6, z), new THREE.Vector3(sx * c45, sy * c45, 0), yaw, 0.35);
  pdcOn(s, new THREE.Vector3(0, 14, 44), new THREE.Vector3(0, 1, 0), 0, 0.3);
  pdcOn(s, new THREE.Vector3(0, -14, -34), new THREE.Vector3(0, -1, 0), Math.PI, 0.3);
  s.cyl(0.5, 10, 'y', 3, 19, 50, 'metal', { tier: 'full', seg: 4 });
  s.sides(12.4, 0, -20);
  s.lamp(0, 0, -106, 'strobe'); s.lamp(0, 0, 76.5, 'strobe'); s.lamp(0, 24, 50, 'ident');
}

/** A point-defence mount set on a face whose outward normal is `n`, its zero yaw toward the bow; ×1.6 on the escort (4 m tall), so it reads on a 185 m hull. */
function pdcOn(s: Spec, at: THREE.Vector3, n: THREE.Vector3, yaw: number, pitch: number): void {
  const zf = new THREE.Vector3(0, 0, 1).addScaledVector(n, -n.z).normalize(), x = new THREE.Vector3().crossVectors(n, zf);
  s.within(new THREE.Matrix4().makeBasis(x, n, zf).scale(new THREE.Vector3(1.6, 1.6, 1.6)).setPosition(at), () => pdc(s, 0, 0, 0, yaw, pitch));
}

/**
 * A point-defence mount (PDC), small and fast-tracking (the owner: *"escort I think is decent though we'll want to dial in
 * pdcs"*): a low ring, a compact housing in the livery, a six-barrel rotary gun with a muzzle shroud, an ammunition box
 * behind, a sensor ball on a stalk beside it. Turned `yaw` (0: toward the bow) and raised `pitch`. 2.6 m to the top.
 */
function pdc(s: Spec, x: number, y: number, z: number, yaw: number, pitch: number): void {
  s.cyl(1.25, 0.6, 'y', x, y + 0.3, z, 'machDk', { seg: 10, tier: 'full' });
  s.box(1.7, 1.2, 1.9, x, y + 1.2, z, 'team', { ry: yaw, tier: 'full' });
  s.box(1.72, 0.25, 1.92, x, y + 1.7, z, 'teamLt', { tier: 'full', ry: yaw });
  const d = new THREE.Vector3(Math.sin(yaw) * Math.cos(pitch), Math.sin(pitch), Math.cos(yaw) * Math.cos(pitch));
  const side = new THREE.Vector3(Math.cos(yaw), 0, -Math.sin(yaw)), up = new THREE.Vector3().crossVectors(d, side).normalize();
  const o = new THREE.Vector3(x, y + 1.25, z).addScaledVector(d, 0.9);
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2, off = side.clone().multiplyScalar(Math.cos(a) * 0.17).addScaledVector(up, Math.sin(a) * 0.17);
    s.rod(o.clone().add(off), o.clone().add(off).addScaledVector(d, 2.4), 0.055, 'nozzle', { tier: 'full', seg: 4 });
  }
  s.rod(o.clone().addScaledVector(d, 1.9), o.clone().addScaledVector(d, 2.5), 0.32, 'machLt', { tier: 'full', seg: 8 });
  s.rod(o.clone().addScaledVector(d, -0.2), o.clone().addScaledVector(d, 0.5), 0.36, 'machDk', { tier: 'full', seg: 8 });
  const back = new THREE.Vector3(x, y + 1.0, z).addScaledVector(new THREE.Vector3(Math.sin(yaw), 0, Math.cos(yaw)), -1.25);
  s.box(0.9, 0.8, 0.7, back.x, back.y, back.z, 'machDk', { tier: 'full', ry: yaw });
  const eye = new THREE.Vector3(x, y, z).addScaledVector(side, 1.15);
  s.rod(eye.clone().setY(y + 0.6), eye.clone().setY(y + 2.0), 0.08, 'metal', { tier: 'full', seg: 4 });
  s.sphere(0.36, eye.x, y + 2.25, eye.z, 'cream', { tier: 'full' });
}

/** The tender, 38 m: the utility craft (it replaces the boarding craft): an armoured cab, two arms, a docking collar, four pods. */
function tender(s: Spec): void {
  s.box(9, 4, 24, 0, -2.5, -2, 'mach');
  gear(s, 10, 13, (_k, r) => [0.8 + r() * 2, 0.8 + r() * 1.6, 1 + r() * 3, -3.5 + r() * 7, -5 + r() * 1.6, -12 + r() * 20]);
  s.side([-14, -0.5, 8, -0.5, 14, 2, 13, 7, 4, 8.4, -14, 8.4], 12, 0, 'team', { chamfer: 0.5 });
  s.box(12.3, 1.2, 18, 0, 6, -3, 'teamLt');
  s.box(9, 1.2, 3.6, 0, 7.2, 8.6, 'window', { tier: 'full', glow: 1, rx: -0.35 });
  s.slit(6.1, 3, -6, 8, 0.35);
  s.text('T-21', 6.1, 4.2, 4, 2.2, 6, 'cream');
  s.mark(6.1, 3.8, -10, 2.6, true);
  // the manipulator arms and their claws, the docking collar
  for (const sx of [-1, 1]) {
    s.box(1.2, 1.2, 9, sx * 3.4, -3.4, 12.5, 'machLt', { rx: 0.25 });
    s.box(1, 1, 7, sx * 3.4, -5, 19.5, 'machLt', { rx: -0.2 });
    for (const cy of [-0.7, 0.7]) s.box(0.5, 0.5, 2.4, sx * 3.4, -4.4 + cy, 23.6, 'machDk', { tier: 'full', rx: -cy * 0.4 });
  }
  s.cyl(2.1, 2.4, 'z', 0, 2.8, 15, 'machDk', { seg: 10 });
  s.torus(2.5, 0.5, 'z', 0, 2.8, 16.3, 'cream');
  // the thruster pods on their outriggers, the drive
  for (const sx of [-1, 1]) for (const z of [-10, 5]) {
    s.box(3, 0.8, 0.8, sx * 7.4, 3, z, 'mach');
    s.cyl(1.2, 5, 'z', sx * 9.4, 3, z, 'team', { seg: 8 });
    s.cyl(0.8, 1.4, 'z', sx * 9.4, 3, z - 3.1, 'nozzle', { r2: 1.1, tier: 'full', seg: 8 });
  }
  for (const sx of [-1, 1]) s.cyl(1, 3, 'z', sx * 2.5, 3, -15.5, 'nozzle', { r2: 1.6, seg: 8 });
  s.sides(10.6, 3, -10);
  s.lamp(0, 8.8, -10, 'strobe'); s.lamp(0, -4, 24.5, 'flood'); s.lamp(0, 8.8, 0, 'ident');
}

/**
 * The vehicle frame's outside measure (metres): the steel cage a vehicle ships in (rendering/vehicleFrame.ts), sized round the
 * longest and tallest vehicle it takes — the BTR (6.7 m long) and the M113 (3.45 m tall with its .50): 7.6 long, 3.5 wide, 4.0
 * tall; 7.2 × 3.1 × 3.5 clear above the deck. The lighters carry them; the base stacks them.
 */
export const FRAME = { length: 7.6, width: 3.5, height: 4.0 };
/** A frame as cargo, drawn simply (its twelve edges and the vehicle's bulk inside); one block for all of them in the far tier. */
function frameCargo(s: Spec, x: number, y: number, z: number, i: number): void {
  const L = FRAME.length, W = FRAME.width, H = FRAME.height, e = 0.22;
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) s.box(e, H, e, x + sx * (W / 2 - e / 2), y + H / 2, z + sz * (L / 2 - e / 2), 'team', { tier: 'full' });
  for (const yy of [e / 2, H - e / 2]) {
    for (const sx of [-1, 1]) s.box(e, e, L, x + sx * (W / 2 - e / 2), y + yy, z, 'team', { tier: 'full' });
    for (const sz of [-1, 1]) s.box(W, e, e, x, y + yy, z + sz * (L / 2 - e / 2), 'team', { tier: 'full' });
  }
  s.box(W - 0.3, 0.12, L - 0.3, x, y + 0.2, z, 'machDk', { tier: 'full' });
  const bulk: Paint[] = ['olive', 'tan', 'olive', 'slate', 'olive', 'tan'];
  s.box(2.7, 2.4 + (i % 2) * 0.5, 5.6 + (i % 3) * 0.5, x, y + 1.5 + (i % 2) * 0.25, z, bulk[i % bulk.length], { tier: 'full' });
}

/**
 * Where each lighter carries its vehicle frames (the frame's floor centre, metres in the ship's frame): the frames sheet hangs the
 * real frames there when a lighter is built without its cargo (`SpecOptions.cargo` false).
 */
export const LIGHTER_SLOTS: Record<'lifter' | 'truss', { x: number; y: number; z: number }[]> = {
  lifter: [-1, 1].flatMap(sx => [-1, 1].map(sz => ({ x: sx * (FRAME.width / 2 + 0.15), y: 0.6, z: -3 + sz * (FRAME.length / 2 + 0.4) }))),
  truss: [-1, 1].flatMap(sx => [-1, 0, 1].map(k => ({ x: sx * (FRAME.width / 2 + 0.15), y: 0.4, z: k * (FRAME.length + 0.6) }))),
};
function cargoAt(s: Spec, kind: keyof typeof LIGHTER_SLOTS, block: [number, number, number, number, number, number]): void {
  if (!s.opts.cargo) return;
  LIGHTER_SLOTS[kind].forEach((p, i) => frameCargo(s, p.x, p.y, p.z, i));
  const [w, h, d, x, y, z] = block;
  s.box(w, h, d, x, y, z, 'team', { tier: 'far' });
}

/** The dropship's nacelle profile (rendering/dropshipLooks.ts NACELLE: z, y about its shaft; intake ahead, nozzle aft), at 1. */
export const NACELLE_PROFILE: readonly number[] = [1.3, -0.33, 1.15, 0.42, 0.45, 0.5, -1.0, 0.45, -1.35, 0.26, -1.35, -0.26, -1.0, -0.42, 0.45, -0.46, 1.15, -0.42];
/**
 * Lighter A's swivelling nacelles (69.10, the owner: "I think it only makes sense if we have downward articulating thrusters
 * to justify it being vtol/stovl ish, runway is very short"): four, two a side on stubs off the hull's flanks — their shafts at
 * x ±`x`, height `y`, the fore and aft pair at `z` — the dropship's nacelle at `scale` (8.7 m long: the dropship's own, 2.7 m
 * on a 12 m ship, in proportion to the lighter's 42), `width` across; the shafts high enough that a nacelle swung down clears the
 * ground.
 */
export const LIFTER_NACELLES = { x: 11.2, y: 6.2, z: [8.5, -10.5] as const, scale: 3.4, width: 2.8 };
/**
 * One of lighter A's nacelles at its shaft (x, y, z), swung down by `tilt` (0 aft, thrusting back: the glide and the climb;
 * 1 down, intake up and nozzle down: the approach, the flare, the touchdown): the dropship's profile, the team's band, the
 * intake's mouth and fan, the nozzle.
 */
export function lifterNacelle(s: Spec, x: number, y: number, z: number, tilt: number): void {
  const k = LIFTER_NACELLES.scale, w = LIFTER_NACELLES.width;
  s.within(new THREE.Matrix4().makeTranslation(x, y, z).multiply(new THREE.Matrix4().makeRotationX(-tilt * Math.PI / 2)), () => {
    s.side(NACELLE_PROFILE.map(v => v * k), w, 0, 'team', { chamfer: 0.12, tier: 'full' });
    s.box(w, 0.9 * k, 2.6 * k, 0, 0, 0, 'team', { tier: 'far' });   // (far: a box)
    s.box(w + 0.04, 0.85 * k, 0.33 * k, 0, 0, -0.9 * k, 'teamLt', { tier: 'full' });   // (the team's band)
    s.cyl(0.35 * k, 0.05 * k, 'z', 0, 0, 1.27 * k, 'rubber', { seg: 12, tier: 'full' });   // (the intake's mouth, its fan inside)
    s.cyl(0.08 * k, 0.12 * k, 'z', 0, 0, 1.2 * k, 'metal', { seg: 8, tier: 'full' });
    for (let i = 0; i < 6; i++) s.box(0.075 * k, 0.29 * k, 0.04, Math.cos(i * Math.PI / 3) * 0.19 * k, Math.sin(i * Math.PI / 3) * 0.19 * k, 1.2 * k, 'machDk', { rz: i * Math.PI / 3 + Math.PI / 2, tier: 'full' });
    s.cyl(0.32 * k, 0.29 * k, 'z', 0, 0, -1.47 * k, 'metal', { r2: 0.37 * k, seg: 10, tier: 'full' });   // (the nozzle)
    s.cyl(0.26 * k, 0.3 * k, 'z', 0, 0, -1.48 * k, 'nozzle', { seg: 10, tier: 'full' });
  });
}

/**
 * Lighter A, the lifting body, 42 m: a squat hull that flies down through the air on its belly and back up on its bells — flat
 * underneath, its back rounded off, two canted fins, four lift fans set in the belly for the landing. Four frames ride inside
 * (two by two) and roll out down the rear ramp (open when it is built without cargo, shut in orbit).
 */
function lifter(s: Spec): void {
  s.side([-18, 0.4, 14, 0.4, 19, 2, 21, 4.5, 19.5, 7, 11, 9.8, -10, 10.4, -18, 9.6], 17, 0, 'team', { chamfer: 1.2 });
  s.box(17.2, 0.7, 36, 0, 4.2, -1, 'teamLt');
  s.box(14, 0.5, 30, 0, 0.3, -1, 'mach');
  gear(s, 10, 37, (_k, r) => [1 + r() * 2, 0.3 + r() * 0.4, 1 + r() * 3, -6 + r() * 12, 0.05, -15 + r() * 28]);
  for (const sx of [-1, 1]) for (const z of [-11, 9]) { s.cyl(2.2, 0.3, 'y', sx * 5, 0.1, z, 'machDk', { seg: 12 }); s.cyl(0.5, 0.35, 'y', sx * 5, 0.08, z, 'machLt', { tier: 'full', seg: 8 }); }
  for (const sx of [-1, 1]) s.box(0.6, 5, 6, sx * 6.5, 11.8, -14, 'team', { rz: sx * 0.45 });
  // 69.10: the four swivelling nacelles on their stubs (drawn aft here; play turns its own)
  for (const sx of [-1, 1]) for (const z of LIFTER_NACELLES.z) {
    s.box(LIFTER_NACELLES.x - LIFTER_NACELLES.width / 2 - 8.3, 1.2, 2.8, sx * (8.3 + (LIFTER_NACELLES.x - LIFTER_NACELLES.width / 2 - 8.3) / 2), LIFTER_NACELLES.y, z, 'teamDk');
    s.cyl(0.55, 0.5, 'x', sx * (LIFTER_NACELLES.x - LIFTER_NACELLES.width / 2 - 0.2), LIFTER_NACELLES.y, z, 'metal', { seg: 10, tier: 'full' });   // (the shaft's collar)
    if (s.opts.nacelles !== false) lifterNacelle(s, sx * LIFTER_NACELLES.x, LIFTER_NACELLES.y, z, 0);
  }
  s.box(8, 1.2, 2.6, 0, 7.6, 17.4, 'window', { tier: 'full', glow: 1, rx: -0.65 });
  s.name(8.56, 6.4, -2, 1.6, 18);
  s.mark(8.56, 6.3, 12.5, 1.6, true);
  s.slit(8.57, 2.4, 2, 10, 0.25);
  for (const x of [-3, 0, 3]) { s.cyl(1.1, 3, 'z', x, 8.6, -19.4, 'nozzle', { r2: 1.6, seg: 8 }); s.torus(1.55, 0.15, 'z', x, 8.6, -20.9, 'coil', { tier: 'full', glow: 1 }); }
  for (const sx of [-1, 1]) s.box(0.5, 0.4, 12, sx * 6.2, 0.2, -1, 'machDk', { tier: 'full' });
  // the rear: the bay's mouth and its ramp (down when unladen)
  s.box(13.2, 7, 0.2, 0, 4.2, -18.05, 'bay');
  if (s.opts.ramp !== false) {
    if (s.opts.cargo) s.box(12.8, 7.2, 0.4, 0, 4.2, -18.3, 'team');
    else s.box(12.4, 0.4, 7.6, 0, 1.6 * 0.5 + 0.1, -18 - 3.6, 'machLt', { rx: -0.2 });
  }
  cargoAt(s, 'lifter', [0.1, 0.1, 0.1, 0, 4, 0]);
  s.sides(8.7, 4.5, -10);
  s.lamp(0, 10.6, -4, 'strobe'); s.lamp(0, 10.5, 6, 'ident'); s.lamp(0, -0.1, 0, 'flood');
}

/**
 * Lighter B, the truss, 38 m: an open cage that clamps its load — six frames, three along and two across — with the cab on a
 * stub forward and four engine pods at its upper corners (a lift fan over a bell each). All structure, nothing to hide the load.
 */
function truss(s: Spec): void {
  const W = FRAME.width + 0.2, H = FRAME.height + 0.7, Z = 1.5 * FRAME.length + 1.6;
  for (const sx of [-1, 1]) for (const y of [0.2, H]) s.box(0.7, 0.7, 2 * Z, sx * (W + 0.35), y, 0, 'team');
  for (let k = -3; k <= 3; k++) {
    const z = (k * Z) / 3;
    for (const sx of [-1, 1]) s.box(0.5, H, 0.5, sx * (W + 0.35), H / 2 + 0.1, z, 'team', { tier: Math.abs(k) === 3 ? 'both' : 'full' });
    s.box(2 * W + 1.2, 0.5, 0.5, 0, H, z, 'team', { tier: 'full' });
    if (k % 3 === 0) s.box(2 * W + 1.2, 0.5, 0.5, 0, 0.2, z, 'team', { tier: 'full' });
  }
  const bl = Z / 3, a = Math.atan2(H - 0.4, bl), diag = Math.hypot(H - 0.4, bl);
  for (const sx of [-1, 1]) for (let k = -3; k < 3; k++) s.box(0.3, 0.3, diag, sx * (W + 0.35), H / 2 + 0.1, ((k + 0.5) * Z) / 3, 'teamDk', { tier: 'full', rx: k % 2 ? a : -a });
  s.box(2 * W + 1.6, 0.5, 2 * Z, 0, H + 0.45, 0, 'teamDk', { tier: 'far' });
  // clamps from the roof to each frame's top castings
  for (const p of LIGHTER_SLOTS.truss) for (const cz of [-1, 1]) s.box(0.35, H - FRAME.height - 0.2, 0.35, p.x, (H + FRAME.height + p.y) / 2 + 0.2, p.z + cz * (FRAME.length / 2 - 0.25), 'machLt', { tier: 'full' });
  // the cab on its stub
  s.box(1.6, 1.6, 4, 0, H - 0.4, Z + 2, 'mach');
  s.side([Z + 3.5, H - 3, Z + 9, H - 3, Z + 11, H - 0.5, Z + 10, H + 2.5, Z + 3.5, H + 3], 6, 0, 'team', { chamfer: 0.3 });
  s.box(5.4, 1.1, 2.2, 0, H + 1.4, Z + 10.4, 'window', { tier: 'full', glow: 1, rx: -0.5 });
  s.box(6.1, 0.5, 6, 0, H - 1.6, Z + 6.5, 'teamLt');
  s.name(3.06, H + 0.2, Z + 6.5, 0.9, 6.5);
  // the engine pods at the upper corners
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) {
    const x = sx * (W + 2.6), z = sz * (Z - 3);
    s.box(2.6, 0.6, 1.2, sx * (W + 1.4), H, z, 'mach', { tier: 'full' });
    s.cyl(2.0, 1.6, 'y', x, H + 0.2, z, 'team', { seg: 12 });
    s.cyl(1.7, 0.2, 'y', x, H + 0.6, z, 'machDk', { tier: 'full', seg: 12 });
    s.cyl(0.7, 2.2, 'z', x, H - 1.1, z - 1.6, 'nozzle', { r2: 1.1, seg: 8 });
    s.torus(1.05, 0.12, 'z', x, H - 1.1, z - 2.75, 'coil', { tier: 'full', glow: 1 });
  }
  cargoAt(s, 'truss', [2 * W, FRAME.height, 2 * Z - 2, 0, FRAME.height / 2 + 0.4, 0]);
  s.sides(W + 4.7, H, -Z + 3);
  s.lamp(0, H + 3.2, Z + 6.5, 'strobe'); s.lamp(0, H + 0.8, 0, 'beacon'); s.lamp(0, H + 3.1, Z + 5, 'ident'); s.lamp(0, -0.2, 0, 'flood');
}

/** What each kind is: its name, its role, its length (m, bow to stern) and builder. */
export const SHIP_KINDS: Record<ShipKind, { name: string; role: string; length: number; build: (s: Spec) => void }> = {
  freighter: { name: 'Carrier A · container freighter', role: 'expeditionary carrier', length: 1485, build: freighter },
  carrier: { name: 'Carrier B · armoured carrier', role: 'expeditionary carrier', length: 1190, build: carrier },
  trawler: { name: 'Carrier C · trawler', role: 'expeditionary carrier', length: 1175, build: trawler },
  escort: { name: 'Escort', role: 'gunship', length: 185, build: escort },
  tender: { name: 'Tender', role: 'utility (and the odd boarding)', length: 40, build: tender },
  lifter: { name: 'Lighter A · lifting body', role: 'the one that lands: four frames inside', length: 42, build: lifter },
  truss: { name: 'Lighter B · truss', role: 'orbit and ship to ship: six frames clamped in a cage', length: 38, build: truss },
};

// ---------------------------------------------------------------------------
// Geometry, material
// ---------------------------------------------------------------------------

/** One ship, both tiers: each one merged geometry with `paint` (linear colour) and `glow` per vertex. */
export interface ShipLook {
  kind: ShipKind;
  full: THREE.BufferGeometry;
  far: THREE.BufferGeometry;
  lamps: Lamp[];
  /** bow to stern, metres (the spec's measure; the geometry's own is `size`) */
  length: number;
  size: THREE.Vector3;
  /** the hull's lit colour on average (the dot a ship becomes past its far tier) */
  tone: THREE.Color;
}

function paintOf(c: Paint, p: UnitPalette): string {
  return c === 'team' ? p.body : c === 'teamDk' ? p.dark : c === 'teamLt' ? p.flash : PAINT[c];
}
/** One tier of a spec as one geometry (`paint` and `glow` per vertex): the ships' and the vehicle frames' (rendering/vehicleFrame.ts). */
export function mergedTier(spec: Spec, p: UnitPalette, far: boolean): THREE.BufferGeometry {
  const geos: THREE.BufferGeometry[] = [];
  const col = new THREE.Color();
  for (const part of spec.parts) {
    if (part.tier !== 'both' && (part.tier === 'far') !== far) continue;
    let g = part.make(far);
    if (g.index) g = g.toNonIndexed();
    for (const name of Object.keys(g.attributes)) if (name !== 'position' && name !== 'normal') g.deleteAttribute(name);
    const n = g.attributes.position.count, paint = new Float32Array(n * 3), glow = new Float32Array(n).fill(part.glow);
    col.set(paintOf(part.c, p));
    for (let i = 0; i < n; i++) { paint[3 * i] = col.r; paint[3 * i + 1] = col.g; paint[3 * i + 2] = col.b; }
    g.setAttribute('paint', new THREE.BufferAttribute(paint, 3));
    g.setAttribute('glow', new THREE.BufferAttribute(glow, 1));
    geos.push(g);
  }
  const merged = mergeGeometries(geos, false)!;
  for (const g of geos) g.dispose();
  merged.computeBoundingBox(); merged.computeBoundingSphere();
  return merged;
}
/** Build one kind's look in the team's palette and livery (both tiers; a few ms for a carrier, so build once per kind and team). */
export function buildShipLook(kind: ShipKind, p: UnitPalette, livery: Livery = { name: 'ORBITUARY', mark: 0 }, opts: SpecOptions = { cargo: true }): ShipLook {
  const spec = new Spec(livery, opts);
  SHIP_KINDS[kind].build(spec);
  const full = mergedTier(spec, p, false), far = mergedTier(spec, p, true);
  const size = full.boundingBox!.getSize(new THREE.Vector3());
  const tone = new THREE.Color(p.body).lerp(new THREE.Color(PAINT.cream), 0.45);
  return { kind, full, far, lamps: spec.lamps, length: SHIP_KINDS[kind].length, size, tone };
}
export function triangles(g: THREE.BufferGeometry): number { return g.attributes.position.count / 3; }

/** The ships' one material: lit by its own sun (so the sky can light a ship the ground's night leaves dark), hazed toward the sky. */
export type ShipMaterial = THREE.ShaderMaterial & { uniforms: {
  uSun: { value: THREE.Vector3 }; uSunColor: { value: THREE.Color }; uLit: { value: number };
  uAmbient: { value: THREE.Color }; uFill: { value: THREE.Vector3 }; uFillColor: { value: THREE.Color };
  uGlow: { value: number }; uHaze: { value: number }; uSky: { value: THREE.Color };
} };
export function shipMaterial(): ShipMaterial {
  return new THREE.ShaderMaterial({
    uniforms: {
      uSun: { value: new THREE.Vector3(0.4, 0.8, 0.3).normalize() }, uSunColor: { value: new THREE.Color(1.6, 1.5, 1.35) }, uLit: { value: 1 },
      uAmbient: { value: new THREE.Color(0.16, 0.17, 0.2) }, uFill: { value: new THREE.Vector3(0, -1, 0) }, uFillColor: { value: new THREE.Color(0.12, 0.14, 0.18) },
      uGlow: { value: 1 }, uHaze: { value: 0 }, uSky: { value: new THREE.Color(0, 0, 0) },
    },
    vertexShader: /* glsl */`
      #include <common>
      #include <logdepthbuf_pars_vertex>
      attribute vec3 paint;
      attribute float glow;
      varying vec3 vPaint;
      varying float vGlow;
      varying vec3 vN;
      void main() {
        vPaint = paint; vGlow = glow;
        vN = normalize(mat3(modelMatrix) * normal);
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        #include <logdepthbuf_vertex>
      }`,
    fragmentShader: /* glsl */`
      #include <common>
      #include <logdepthbuf_pars_fragment>
      uniform vec3 uSun; uniform vec3 uSunColor; uniform float uLit;
      uniform vec3 uAmbient; uniform vec3 uFill; uniform vec3 uFillColor;
      uniform float uGlow; uniform float uHaze; uniform vec3 uSky;
      varying vec3 vPaint;
      varying float vGlow;
      varying vec3 vN;
      void main() {
        #include <logdepthbuf_fragment>
        vec3 n = normalize(vN);
        vec3 light = uAmbient + uSunColor * (uLit * max(dot(n, uSun), 0.0)) + uFillColor * max(dot(n, uFill), 0.0);
        vec3 c = vPaint * light + vPaint * (vGlow * uGlow);
        gl_FragColor = vec4(mix(c, uSky, uHaze), 1.0);
        #include <tonemapping_fragment>
        #include <colorspace_fragment>
      }`,
  }) as ShipMaterial;
}
